'use client';
import { useCallback, useEffect, useState } from 'react';
import { ButtonIcon } from '@workspace/ui/common';
import { NavigationBar, HeaderSearch, Logo, PrimaryButton, type PrimaryButtonIconName } from './ui';
import { MobileMegaMenu } from '../MegaMenu/Mobile/MobileMegaMenu';
import { usePathname } from 'next/navigation';
import { cn } from '@workspace/lib';
import type { IHeaderData } from '@workspace/interfaces/header';

export interface HeaderProps {
  className?: string;
  onActivePathChange?: (newPath: string) => void;
  headerData?: IHeaderData;
  loginButton?: IHeaderData['loginButton'] | null;
  showSearch?: boolean;
  primaryButtonIconName?: PrimaryButtonIconName;
}

export const Header = ({
  className = '',
  onActivePathChange,
  headerData,
  loginButton,
  showSearch = true,
  primaryButtonIconName,
}: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  const [activePath, setActivePath] = useState(pathname);

  useEffect(() => {
    setActivePath(pathname);
  }, [pathname]);

  // Lock body scroll and prevent background scroll when mobile menu is open
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const restoreY = window.scrollY || window.pageYOffset || 0;

    Object.assign(document.body.style, {
      position: 'fixed',
      top: `-${restoreY}px`,
      left: '0',
      right: '0',
      width: '100%',
      overflow: 'hidden',
    });

    const preventScroll = (event: Event) => {
      event.preventDefault();
    };

    document.addEventListener('wheel', preventScroll, { passive: false });
    document.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      ['overflow', 'position', 'top', 'left', 'right', 'width'].forEach((p) => document.body.style.removeProperty(p));
      window.scrollTo(0, restoreY);
      document.removeEventListener('wheel', preventScroll as EventListener);
      document.removeEventListener('touchmove', preventScroll as EventListener);
    };
  }, [isMobileMenuOpen]);

  const handleMobileMenuClose = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const onSearchToggle = useCallback((isOpen: boolean) => {
    if (isOpen) {
      setIsMobileMenuOpen(false);
    }
  }, []);

  const hasNavigation = (headerData?.navigationItems?.length ?? 0) > 0;
  const resolvedLoginButton = headerData?.loginButton || loginButton;
  const hasHeaderButtons = Boolean(headerData?.primaryButton?.isActive || resolvedLoginButton?.isActive);

  return (
    <header className={cn('bg-white sticky top-0 z-[80] h-21 max-md:h-18 border-b border-b-secondary-100', className)}>
      <div className="header-porta-container p-md md:px-4xl relative h-full border-b border-b-secondary-100 sm:border-b-0 z-[70]">
        <div className="flex items-center justify-between h-full">
          <div className="flex-shrink-0">
            <Logo headerData={headerData} />
          </div>

          <div className="right-header-container xl:flex justify-between items-center gap-sm hidden">
            {showSearch && <HeaderSearch onSearchToggle={onSearchToggle} />}

            {/* Desktop Navigation */}
            {hasNavigation && (
              <div className="hidden xl:flex items-center space-x-lg ml-sm -mb-px">
                <NavigationBar
                  navigationItems={headerData?.navigationItems || []}
                  activePath={activePath}
                  onActivePathChange={onActivePathChange}
                />
              </div>
            )}

            {/* Search and Header Buttons */}
            {hasHeaderButtons && (
              <div className="hidden xl:flex items-center space-x-md">
                {headerData?.primaryButton?.isActive && (
                  <PrimaryButton primaryButton={headerData?.primaryButton} iconName={primaryButtonIconName} />
                )}
                {resolvedLoginButton?.isActive && (
                  <PrimaryButton primaryButton={resolvedLoginButton} iconName={primaryButtonIconName} />
                )}
              </div>
            )}
          </div>

          {/* Mobile Header */}
          <div className="xl:hidden flex items-center justify-end w-full">
            {/* Right side - Search and Menu */}
            <div className="flex items-center gap-1.5 bg-transparent">
              {/* Search */}
              {showSearch && <HeaderSearch onSearchToggle={onSearchToggle} />}

              {/* Hamburger Menu / Close Button */}
              {hasNavigation && (
                <ButtonIcon
                  iconName={isMobileMenuOpen ? 'cancelIcon' : 'HamburgerIcon'}
                  iconSize={isMobileMenuOpen ? 18 : 24}
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="text-secondary-400 hover:text-primary-500 transition-colors duration-200 active:border-none size-6"
                  aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Overlay */}
      {hasNavigation && (
        <MobileMegaMenu
          isOpen={isMobileMenuOpen}
          onClose={handleMobileMenuClose}
          activePath={activePath}
          onActivePathChange={onActivePathChange}
          navigationItems={headerData?.navigationItems}
          primaryButton={headerData?.primaryButton}
          loginButton={resolvedLoginButton}
        />
      )}
    </header>
  );
};
